import { useState, useRef, useEffect, useCallback, type FormEvent, type ChangeEvent } from "react";
import { Send, Loader2, Paperclip, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { ChatMessage } from "@/components/chat/ChatMessage";
import type { ChatMessage as ChatMessageType, Attachment } from "@/hooks/useChat";
import type { Item } from "@/lib/types";
import { formatError } from "@/lib/utils";

const ACCEPT = "image/png,image/jpeg,image/gif,image/webp,application/pdf";
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function fileToAttachment(file: File): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      resolve({
        type: file.type.startsWith("image/") ? "image" : "document",
        media_type: file.type,
        data: base64,
        name: file.name,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ItemChat({ item }: { item: Item }) {
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pendingMessages, setPendingMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load persisted messages from DB
  const { data: savedMessages } = useQuery({
    queryKey: ["item-chat", item.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("item_chat_messages")
        .select("role, content")
        .eq("item_id", item.id)
        .order("created_at");
      if (error) throw error;
      return (data || []) as ChatMessageType[];
    },
    enabled: !!item.id,
  });

  const messages: ChatMessageType[] = [...(savedMessages || []), ...pendingMessages];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) continue;
      const att = await fileToAttachment(file);
      setAttachments((prev) => [...prev, att]);
    }
    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const send = useCallback(
    async (text: string, atts?: Attachment[]) => {
      if (!user) return;
      const userMsg: ChatMessageType = { role: "user", content: text, attachments: atts };
      setPendingMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const contextPrefix = `[Context: I'm looking at the item "${item.title}" — priority: ${item.priority}, status: ${item.status}${item.project_tag ? `, project: ${item.project_tag}` : ""}${item.notes ? `, notes: ${item.notes}` : ""}, item id: ${item.id}]\n\n`;

        const allMessages = [...(savedMessages || []), ...pendingMessages, userMsg];
        const messagesForApi = allMessages.map((m, i) => ({
          role: m.role,
          content:
            i === 0 && m.role === "user"
              ? contextPrefix + m.content
              : m.content,
        }));

        const { data, error } = await supabase.functions.invoke("chat", {
          body: {
            messages: messagesForApi,
            user_id: user.id,
            attachments: atts?.map((a) => ({
              type: a.type,
              media_type: a.media_type,
              data: a.data,
            })),
          },
        });

        if (error) throw error;

        const assistantMsg: ChatMessageType = {
          role: "assistant",
          content: data.response || "Hmm, couldn't process that.",
        };

        // Save both messages to DB
        await supabase.from("item_chat_messages").insert([
          { item_id: item.id, user_id: user.id, role: "user", content: text },
          {
            item_id: item.id,
            user_id: user.id,
            role: "assistant",
            content: assistantMsg.content,
          },
        ]);

        setPendingMessages([]);
        queryClient.invalidateQueries({ queryKey: ["item-chat", item.id] });
        queryClient.invalidateQueries({ queryKey: ["items"] });
        queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      } catch (err) {
        setPendingMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Error: ${formatError(err)}` },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [savedMessages, pendingMessages, user, item, queryClient]
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if ((!text && attachments.length === 0) || isLoading) return;
    setInput("");
    const atts = attachments.length > 0 ? [...attachments] : undefined;
    setAttachments([]);
    send(text, atts);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="flex flex-col h-full border-t border-border">
      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            Ask me anything about this item...
          </div>
        )}
        {messages.map((msg, i) => (
          <ChatMessage key={i} message={msg} />
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted text-muted-foreground rounded-lg px-3 py-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-border px-4 py-3">
        {/* Attachment previews */}
        {attachments.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {attachments.map((att, i) => (
              <div key={i} className="relative">
                {att.type === "image" ? (
                  <img
                    src={`data:${att.media_type};base64,${att.data}`}
                    alt={att.name}
                    className="h-12 rounded border border-border"
                  />
                ) : (
                  <div className="flex items-center gap-1 bg-muted rounded px-2 py-1 text-xs border border-border">
                    <Paperclip className="h-3 w-3" />
                    {att.name}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  className="absolute -top-1.5 -right-1.5 bg-background rounded-full"
                >
                  <XCircle className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="shrink-0 h-9 w-9"
            title="Attach file"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this item..."
            rows={1}
            className="flex-1 resize-none bg-muted rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary min-h-[40px] max-h-[100px]"
            style={{ fieldSizing: "content" } as React.CSSProperties}
          />
          <Button
            type="submit"
            size="icon"
            className="h-10 w-10 rounded-full shrink-0"
            disabled={isLoading || (!input.trim() && attachments.length === 0)}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
